<script setup>
import {computed, ref, inject, defineProps} from 'vue';
import {validSelection} from '@/core/util/validation/rules';
import {APIService} from '@/core/util/services/api.service';
import useSort from '@ohrm/core/util/composable/useSort';
import {navigate} from '@ohrm/core/util/helper/navigation';
import usePaginate from '@ohrm/core/util/composable/usePaginate';
import DeleteConfirmationDialog from '@ohrm/components/dialogs/DeleteConfirmationDialog';
import ReportAutocomplete from '@/orangehrmPimPlugin/components/ReportAutocomplete';
import usei18n from '@/core/util/composable/usei18n';

const props = defineProps({
  trainingType: {
    type: String,
    required: true,
  },
});

const $toast = inject('$toast');

const deleteDialog = ref(null);
const defaultFilters = ref({
  report: null,
});
let filters = ref({...defaultFilters.value});
const defaultSortOrder = ref({
  'report.name': 'ASC',
});

const checkedItems = ref([]);
const rules = ref({
  report: [validSelection],
});

const {sortDefinition, sortField, sortOrder, onSort} = useSort({
  sortDefinition: defaultSortOrder.value,
});

const serializedFilters = computed(() => {
  return {
    reportId: filters.value.report?.id,
    sortField: sortField.value,
    sortOrder: sortOrder.value,
  };
});

const http = new APIService(
  window.appGlobal.baseUrl,
  `/api/v2/training/${props.trainingType}/reports`,
);

const {
  showPaginator,
  currentPage,
  total,
  pages,
  pageSize,
  response,
  execQuery,
} = usePaginate(http, {
  query: serializedFilters,
});

let isLoading = false;

onSort(execQuery);

const onClickAdd = () => {
  navigate(`/training/${props.trainingType}/addTrainingReport`);
};
const onClickEdit = item => {
  navigate(`/training/${props.trainingType}/editTrainingReport/{id}`, {
    id: item.id,
  });
};
const onClickView = item => {
  navigate(`/training/${props.trainingType}/viewTrainingReport/{id}`, {
    id: item.id,
  });
};
const resetDataTable = async () => {
  checkedItems.value = [];
  await execQuery();
};
const deleteItems = items => {
  if (items instanceof Array) {
    isLoading = true;
    http
      .request({
        method: 'DELETE',
        url: '/api/v2/pim/reports/defined',
        data: {
          ids: items,
        },
      })
      .then(() => {
        return $toast.deleteSuccess();
      })
      .then(() => {
        isLoading = false;
        resetDataTable();
      });
  }
};
const onClickDeleteSelected = () => {
  const ids = [];
  checkedItems.value.forEach(index => {
    ids.push(response.value?.data[index].id);
  });
  deleteDialog.value.showDialog().then(confirmation => {
    if (confirmation === 'ok') {
      deleteItems(ids);
    }
  });
};
const onClickDelete = item => {
  deleteDialog.value.showDialog().then(confirmation => {
    if (confirmation === 'ok') {
      deleteItems([item.id]);
    }
  });
};
const filterItems = async () => {
  await execQuery();
};
const onClickReset = () => {
  filters = {...defaultFilters};
  filterItems();
};

const headers = ref([
  {
    name: 'name',
    slot: 'title',
    title: usei18n().$t('general.name'),
    style: {flex: '85%'},
    sortField: 'report.name',
  },
  {
    name: 'actions',
    title: usei18n().$t('general.actions'),
    slot: 'action',
    style: {flex: '15%'},
    cellType: 'oxd-table-cell-actions',
    cellConfig: {
      delete: {
        onClick: onClickDelete,
        component: 'oxd-icon-button',
        props: {
          name: 'trash',
        },
      },
      edit: {
        onClick: onClickEdit,
        props: {
          name: 'pencil-fill',
        },
      },
      view: {
        onClick: onClickView,
        props: {
          name: 'file-text-fill',
        },
      },
    },
  },
]);
</script>

<template>
  <div class="orangehrm-background-container">
    <oxd-table-filter :filter-title="$t('general.training_reports')">
      <oxd-form @submitValid="filterItems">
        <oxd-form-row>
          <oxd-grid :cols="2" class="orangehrm-full-width-grid">
            <oxd-grid-item>
              <report-autocomplete
                v-model="filters.report"
                :rules="rules.report"
              ></report-autocomplete>
            </oxd-grid-item>
          </oxd-grid>
        </oxd-form-row>

        <oxd-divider />

        <oxd-form-actions>
          <oxd-button
            display-type="ghost"
            :label="$t('general.reset')"
            @click="onClickReset"
          />
          <oxd-button
            class="orangehrm-left-space"
            display-type="secondary"
            :label="$t('general.search')"
            type="submit"
          />
        </oxd-form-actions>
      </oxd-form>
    </oxd-table-filter>

    <br />

    <div class="orangehrm-paper-container">
      <div class="orangehrm-header-container">
        <oxd-button
          :label="$t('general.add')"
          icon-name="plus"
          display-type="secondary"
          @click="onClickAdd"
        />
      </div>
      <table-header
        :selected="checkedItems.length"
        :total="total"
        :loading="isLoading"
        @delete="onClickDeleteSelected"
      ></table-header>
      <div class="orangehrm-container">
        <oxd-card-table
          v-model:selected="checkedItems"
          v-model:order="sortDefinition"
          :headers="headers"
          :items="response?.data"
          :selectable="true"
          :clickable="false"
          :loading="isLoading"
          row-decorator="oxd-table-decorator-card"
        />
      </div>
      <div class="orangehrm-bottom-container">
        <oxd-pagination
          v-if="showPaginator"
          v-model:current="currentPage"
          :length="pages"
        />
      </div>
    </div>

    <delete-confirmation-dialog ref="deleteDialog"></delete-confirmation-dialog>
  </div>
</template>
